import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Users, UserPlus, Heart, Baby, User, Check, AlertTriangle, GitMerge } from "lucide-react";
import { guessGender } from "../lib/nameGender";
import { kinshipLabel, descendantSet } from "../lib/layout";

const REL_META = {
  parent: { label: "Parent", icon: Users, verb: "the parent of" },
  child: { label: "Child", icon: Baby, verb: "a child of" },
  spouse: { label: "Spouse / Partner", icon: Heart, verb: "the spouse of" },
  sibling: { label: "Sibling", icon: UserPlus, verb: "a sibling of" },
};

const shortName = (m) => m?.nickname || m?.full_name || m?.first_name || "this person";

const normName = (v) => (v || "").trim().toLowerCase().replace(/\s+/g, " ");
const looksSame = (a, b) => {
  if (!a || !b) return false;
  const na = normName(a.full_name), nb = normName(b.full_name);
  if (na && na === nb) return true;
  const ta = na.split(" "), tb = nb.split(" ");
  return ta.length >= 2 && tb.length >= 2 && ta[0] === tb[0] && ta[ta.length - 1] === tb[tb.length - 1];
};

export function QuickConnectDialog({ open, onClose, anchor, members = [], relation: fixedRelation = null, existing = null, onConfirm, onMerge }) {
  const [relation, setRelation] = useState(fixedRelation);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(existing); // an existing member object, or null (=> create new)
  const [gender, setGender] = useState("");
  const [genderTouched, setGenderTouched] = useState(false);
  const [marryExisting, setMarryExisting] = useState(true);
  const [shareChildren, setShareChildren] = useState(true);
  const [linkCouple, setLinkCouple] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setRelation(fixedRelation);
      setQuery(existing ? shortName(existing) : "");
      setPicked(existing || null);
      setGender(existing?.gender || "");
      setGenderTouched(false);
      setMarryExisting(true);
      setShareChildren(true);
      setLinkCouple(true);
      setBusy(false);
    }
  }, [open, fixedRelation, existing]);

  const byId = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m])), [members]);

  // Smart gender guess from the typed first name (until the user chooses manually).
  useEffect(() => {
    if (genderTouched || picked || existing) return;
    const g = guessGender(query);
    setGender(g);
  }, [query, genderTouched, picked, existing]);

  // Drag-to-connect: if the two existing people look like the same person, offer to merge.
  const mergeCandidate = existing && anchor && looksSame(existing, anchor);
  const mergeConfidence = mergeCandidate ? (normName(existing.full_name) === normName(anchor.full_name) ? "likely" : "possible") : null;

  const subjectMember = picked || existing || null;

  // Impossible-link guard: block cycles (someone becoming their own ancestor/descendant).
  const cycleError = useMemo(() => {
    if (!relation || !anchor || !subjectMember) return null;
    if (subjectMember.id === anchor.id) return "You can't connect someone to themselves";
    if (relation === "parent" && descendantSet(members, anchor.id).has(subjectMember.id))
      return `${shortName(subjectMember)} is already a descendant of ${shortName(anchor)} — that would loop the tree`;
    if (relation === "child" && descendantSet(members, subjectMember.id).has(anchor.id))
      return `${shortName(anchor)} is already a descendant of ${shortName(subjectMember)} — that would loop the tree`;
    return null;
  }, [relation, anchor, subjectMember, members]);

  // Relationship preview: how the subject will relate to a notable second-order relative.
  const previewKin = useMemo(() => {
    if (!relation || !anchor || cycleError) return null;
    // simulate the new link on a shallow copy of the graph
    const sim = members.map((m) => ({ ...m, parent_ids: [...(m.parent_ids || [])], spouse_ids: [...(m.spouse_ids || [])] }));
    const byIdSim = Object.fromEntries(sim.map((m) => [m.id, m]));
    const subjId = subjectMember ? subjectMember.id : "__new__";
    if (!byIdSim[subjId]) { const s = { id: subjId, full_name: query.trim() || "New person", gender, parent_ids: [], spouse_ids: [] }; sim.push(s); byIdSim[subjId] = s; }
    const a = byIdSim[anchor.id]; const s = byIdSim[subjId];
    if (!a) return null;
    if (relation === "parent") a.parent_ids.push(subjId);
    else if (relation === "child") s.parent_ids = [anchor.id];
    else if (relation === "spouse") { s.spouse_ids.push(anchor.id); a.spouse_ids.push(subjId); }
    else if (relation === "sibling") s.parent_ids = [...a.parent_ids];
    // pick a reference person (a child of anchor first, then a parent) that isn't the subject/anchor
    const refs = [
      ...sim.filter((m) => (m.parent_ids || []).includes(anchor.id) && m.id !== subjId),
      ...(a.parent_ids || []).map((pid) => byIdSim[pid]).filter(Boolean),
      ...(anchor.spouse_ids || []).map((sid) => byIdSim[sid]).filter(Boolean),
    ];
    for (const ref of refs) {
      if (!ref || ref.id === subjId || ref.id === anchor.id) continue;
      const kin = kinshipLabel(sim, ref.id, subjId);
      if (kin) return { name: shortName(ref), kin };
    }
    return null;
  }, [relation, anchor, subjectMember, members, query, gender, cycleError]);

  // existing relatives context for the follow-up prompts
  const existingParents = useMemo(() => (anchor?.parent_ids || []).map((id) => byId[id]).filter(Boolean), [anchor, byId]);
  const anchorChildren = useMemo(() => (anchor ? members.filter((m) => (m.parent_ids || []).includes(anchor.id)) : []), [anchor, members]);
  const anchorSpouses = useMemo(() => (anchor?.spouse_ids || []).map((id) => byId[id]).filter(Boolean), [anchor, byId]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || (picked && shortName(picked).toLowerCase() === q)) return [];
    const hit = (m) => [m.full_name, m.nickname, m.first_name].some((v) => (v || "").toLowerCase().includes(q));
    return members.filter((m) => m.id !== anchor?.id && hit(m)).slice(0, 6);
  }, [query, members, anchor, picked]);

  // Suggested match: soft duplicate flag when typing a NEW name that closely matches someone.
  const dupeMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || picked) return null;
    const norm = (v) => (v || "").trim().toLowerCase();
    return members.find((m) => m.id !== anchor?.id && (norm(m.full_name) === q || norm(m.nickname) === q || norm(m.first_name) === q)) || null;
  }, [query, members, anchor, picked]);

  // Confidence hint for the soft duplicate flag: exact full-name = "likely", otherwise "possible".
  const dupeConfidence = useMemo(() => {
    if (!dupeMatch) return null;
    return normName(dupeMatch.full_name) === normName(query) ? "likely" : "possible";
  }, [dupeMatch, query]);

  const genderTerm = (g, rel) => {
    const s = (g || "").toLowerCase();
    const m = s.startsWith("m"), f = s.startsWith("f");
    return { parent: m ? "father" : f ? "mother" : "parent", child: m ? "son" : f ? "daughter" : "child", spouse: m ? "husband" : f ? "wife" : "partner", sibling: m ? "brother" : f ? "sister" : "sibling" }[rel];
  };
  const subjGender = picked?.gender || gender;

  const canConfirm = relation && (picked || query.trim().length > 0) && !cycleError;

  const confirm = async () => {
    if (!canConfirm || busy) return;
    setBusy(true);
    try {
      await onConfirm({
        relation,
        targetId: picked ? picked.id : null,
        newName: picked ? null : query.trim(),
        gender: gender || null,
        marryExisting: relation === "parent" && existingParents.length > 0 ? marryExisting : false,
        shareChildren: relation === "spouse" && anchorChildren.length > 0 ? shareChildren : false,
        linkCouple: relation === "child" && anchorSpouses.length > 0 ? linkCouple : true,
      });
      onClose();
    } catch { /* parent shows toast */ } finally { setBusy(false); }
  };

  if (!anchor) return null;
  const anchorName = shortName(anchor);
  const draggedName = existing ? shortName(existing) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-sm" data-testid="quick-connect-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#082D4A]">
            {existing ? "Connect two people" : `Add a relative to ${anchorName}`}
          </DialogTitle>
          <DialogDescription>
            {existing
              ? <>How is <span className="font-semibold text-[#082D4A]">{draggedName}</span> related to <span className="font-semibold text-[#082D4A]">{anchorName}</span>?</>
              : <>Type a name — if they're already in your tree it'll suggest them.</>}
          </DialogDescription>
        </DialogHeader>

        {/* Merge duplicates — when a dragged person looks like the same person as the anchor */}
        {mergeCandidate && onMerge && (
          <div className="flex items-center justify-between gap-2 rounded-xl border border-[#D4A017]/50 bg-[#D4A017]/10 p-2.5 text-xs" data-testid="quick-connect-merge-banner">
            <span className="flex items-center gap-1.5 text-[#8a6d10]"><GitMerge size={14} /> <span>{mergeConfidence === "likely" ? "Very likely" : "Possibly"} the <span className="font-semibold">same person</span></span></span>
            <button type="button" onClick={() => onMerge(anchor.id, existing.id)} data-testid="quick-connect-merge" className="shrink-0 rounded-full bg-[#082D4A] px-2.5 py-1 font-medium text-white hover:bg-[#08223A]">Merge into one</button>
          </div>
        )}

        {/* Relation chooser (shown when not fixed, e.g. drag-to-connect) */}
        {!fixedRelation && (
          <div className="grid grid-cols-2 gap-2" data-testid="quick-connect-relations">
            {Object.entries(REL_META).map(([key, meta]) => {
              const Icon = meta.icon;
              return (
                <button key={key} type="button" onClick={() => setRelation(key)} data-testid={`quick-rel-${key}`}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors ${relation === key ? "border-[#082D4A] bg-[#082D4A] text-white" : "border-[#D8E0E8] bg-white text-[#082D4A] hover:bg-[#E8EEF3]"}`}>
                  <Icon size={16} /> {existing ? draggedName?.split(" ")[0] : ""} {meta.label}
                </button>
              );
            })}
          </div>
        )}

        {relation && (
          <p className="text-sm text-[#082D4A]" data-testid="quick-connect-summary">
            <span className="font-medium">{existing ? draggedName : (picked ? shortName(picked) : (query.trim() || "This person"))}</span> will be <span className="font-semibold text-[#D4A017]">{anchorName}'s {genderTerm(subjGender, relation)}</span>.
          </p>
        )}
        {relation && previewKin && (
          <p className="-mt-1 text-xs text-[#64748B]" data-testid="quick-connect-preview-kin">
            …and {existing ? draggedName : (picked ? shortName(picked) : (query.trim() || "they"))} becomes <span className="font-medium text-[#082D4A]">{previewKin.name}'s {previewKin.kin}</span>.
          </p>
        )}
        {cycleError && (
          <p className="flex items-center gap-1.5 rounded-xl border border-red-300 bg-red-50 p-2.5 text-xs font-medium text-red-700" data-testid="quick-connect-cycle-error">
            <AlertTriangle size={14} /> {cycleError}
          </p>
        )}

        {/* Name entry with autocomplete (hidden in drag "connect existing" mode) */}
        {!existing && relation && (
          <div className="relative">
            <Input
              autoFocus
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
              placeholder="Start typing a name…"
              data-testid="quick-connect-name-input"
            />
            {suggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-[#D8E0E8] bg-white shadow-lg" data-testid="quick-connect-suggestions">
                {suggestions.map((m) => (
                  <button key={m.id} type="button" onClick={() => { setPicked(m); setQuery(shortName(m)); }} data-testid={`quick-suggestion-${m.id}`}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#E8EEF3]">
                    <User size={14} className="text-[#64748B]" />
                    <span className="font-medium text-[#082D4A]">{m.full_name}</span>
                    {m.nickname && <span className="text-xs text-[#64748B]">"{m.nickname}"</span>}
                    {m.birth_date && <span className="text-xs text-[#64748B]">({String(m.birth_date).slice(0, 4)})</span>}
                  </button>
                ))}
              </div>
            )}
            {/* Suggested match — soft duplicate flag */}
            {!picked && dupeMatch && (
              <div className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-[#D4A017]/40 bg-[#D4A017]/5 p-2.5 text-xs" data-testid="quick-connect-dupe-flag">
                <span className="flex items-center gap-1.5 text-[#8a6d10]"><AlertTriangle size={13} /> <span><span className="rounded-full bg-[#D4A017]/20 px-1.5 py-0.5 font-semibold text-[#8a6d10]" data-testid="quick-connect-dupe-confidence">{dupeConfidence === "likely" ? "Likely match" : "Possible match"}</span> — <span className="font-semibold">{dupeMatch.full_name}</span> may already be in your tree</span></span>
                <button type="button" onClick={() => { setPicked(dupeMatch); setQuery(shortName(dupeMatch)); }} data-testid="quick-connect-link-instead" className="shrink-0 rounded-full bg-[#082D4A] px-2.5 py-1 font-medium text-white hover:bg-[#08223A]">Link to them</button>
              </div>
            )}
            {picked ? (
              <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-[#059669]" data-testid="quick-connect-existing-note"><Check size={13} /> Linking to existing {shortName(picked)}</p>
            ) : query.trim() ? (
              <p className="mt-1.5 text-xs text-[#64748B]" data-testid="quick-connect-new-note">Will create a new person "{query.trim()}".</p>
            ) : null}
            {/* optional gender for a NEW person — powers the relationship hint */}
            {!picked && query.trim() && (
              <div className="mt-2 flex items-center gap-2" data-testid="quick-connect-gender">
                <span className="text-xs text-[#64748B]">They are:</span>
                {[["male", "Male"], ["female", "Female"]].map(([val, lbl]) => (
                  <button key={val} type="button" onClick={() => { setGenderTouched(true); setGender((g) => (g === val ? "" : val)); }} data-testid={`quick-gender-${val}`}
                    className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${gender === val ? "border-[#082D4A] bg-[#082D4A] text-white" : "border-[#D8E0E8] bg-white text-[#082D4A] hover:bg-[#E8EEF3]"}`}>{lbl}</button>
                ))}
                {!genderTouched && gender && <span className="text-[10px] italic text-[#94A3B8]" data-testid="quick-gender-guess-hint">best guess — tap to change</span>}
              </div>
            )}
          </div>
        )}

        {/* Smart follow-up prompts */}
        {relation === "parent" && existingParents.length > 0 && (
          <label className="flex items-center gap-2.5 rounded-xl border border-[#D8E0E8] bg-[#F4F6F8] p-3 text-sm" data-testid="quick-marry-toggle">
            <Checkbox checked={marryExisting} onCheckedChange={(v) => setMarryExisting(!!v)} data-testid="quick-marry-checkbox" />
            <span className="text-[#475569]">Married to <span className="font-medium text-[#082D4A]">{existingParents.map(shortName).join(" & ")}</span>? (place them side-by-side as a couple)</span>
          </label>
        )}
        {relation === "spouse" && anchorChildren.length > 0 && (
          <label className="flex items-center gap-2.5 rounded-xl border border-[#D8E0E8] bg-[#F4F6F8] p-3 text-sm" data-testid="quick-share-children-toggle">
            <Checkbox checked={shareChildren} onCheckedChange={(v) => setShareChildren(!!v)} data-testid="quick-share-children-checkbox" />
            <span className="text-[#475569]">Do they share all of <span className="font-medium text-[#082D4A]">{anchorName}</span>'s {anchorChildren.length} {anchorChildren.length === 1 ? "child" : "children"}?</span>
          </label>
        )}
        {relation === "child" && anchorSpouses.length > 0 && (
          <label className="flex items-center gap-2.5 rounded-xl border border-[#D8E0E8] bg-[#F4F6F8] p-3 text-sm" data-testid="quick-link-couple-toggle">
            <Checkbox checked={linkCouple} onCheckedChange={(v) => setLinkCouple(!!v)} data-testid="quick-link-couple-checkbox" />
            <span className="text-[#475569]">Also make <span className="font-medium text-[#082D4A]">{anchorSpouses.map(shortName).join(" & ")}</span> a parent</span>
          </label>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} data-testid="quick-connect-cancel">Cancel</Button>
          <Button onClick={confirm} disabled={!canConfirm || busy} data-testid="quick-connect-confirm" className="bg-[#082D4A] hover:bg-[#08223A]">
            {busy ? "Saving…" : "Connect"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
